import React from 'react';
import { CommonVendorsTable } from './CommonVendorsTable';
import { getColumnDefs, PAGE_SIZE } from './utils';
import isEqual from 'lodash/isEqual';
import { PermissionsContext } from 'contexts/PermissionsContext';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import CategorisationDialog from 'Explore/CategorisationDialog';
import {
	getInvestigateCommonVendorsData,
	changeCategorisation,
	changeCategorisationIndirectClient,
} from 'utils/apiRequests';
import { convertToArray, isCustomCategory } from 'Details/utils';
import { InvestigateContext } from '../../context';
import {
	SPEND_TYPE_LABELS,
	BREAKDOWN_BY,
	SPEND_TYPE_VALUE,
	TAXONOMY_TYPES,
	SPEND_TYPE as SPEND_TYPE_PARAM,
} from '../../utils';

export class CommonVendorsTableContainer extends React.Component {
	state = {
		data: [],
		totalCount: 0,
		isLoading: false,
		headers: [],
		page: 1,
		sortModel: null,
		categorisationModalData: null,
		changeStatusModalData: null,
		moveToBinModalData: null,
	};

	static contextType = InvestigateContext;

	componentDidMount() {
		this.fetchData();
	}

	componentDidUpdate(prevProps) {
		const { queryParams } = this.props;

		if (!isEqual(queryParams, prevProps.queryParams)) {
			this.setState({ page: 1, sortModel: null }, this.fetchData);
		}
	}

	getSortParams = () => {
		const { headers, sortModel } = this.state;

		if (!sortModel) {
			return {};
		}

		const column = headers.find(item => item.colName === sortModel.colId);

		return {
			sortDirection: sortModel && sortModel.sort,
			sortIndex: column && column.sortIndex,
		};
	};

	getApiParams = () => {
		const { queryParams, spendType } = this.props;
		const { page } = this.state;

		return {
			...queryParams,
			...this.getSortParams(),
			spendType,
			take: PAGE_SIZE,
			skip: PAGE_SIZE * (page - 1),
		};
	};

	fetchData = () => {
		const { page, data } = this.state;

		this.setState({ isLoading: true });

		return getInvestigateCommonVendorsData(this.getApiParams())
			.then(({ headers, body, totalCount }) => {
				this.setState({
					data: page === 1 ? body : [...data, ...body],
					headers,
					totalCount,
					isLoading: false,
				});
			})
			.catch(() => {
				this.setState({ isLoading: false });
			});
	};

	getColumnDefs = () => {
		const { spendFormatter, queryParams } = this.props;
		const { sortModel, totalCount, headers } = this.state;
		const dimensionHeaders =
			queryParams.splitSpendByDimension &&
			queryParams.splitSpendByDimension !== BREAKDOWN_BY.NONE
				? headers.slice(2)
				: null;

		return getColumnDefs(
			dimensionHeaders,
			spendFormatter,
			sortModel,
			totalCount
		);
	};

	handleLoadMore = () => {
		const { page } = this.state;

		this.setState({ page: page + 1 }, this.fetchData);
	};

	handleSortChanged = ([sortModel]) => {
		if (sortModel) {
			this.setState({ sortModel, page: 1 }, this.fetchData);
		}
	};

	isCustomCategoryRow = row => {
		const { queryParams, spendType } = this.props;

		if (spendType != null) {
			const taxonomyType =
				spendType === SPEND_TYPE_PARAM.DIRECT
					? queryParams.directTaxonomyType
					: queryParams.indirectTaxonomyType;

			return taxonomyType === TAXONOMY_TYPES.CLIENT;
		}

		const taxonomyType = row.directSpend
			? queryParams.directTaxonomyType
			: queryParams.indirectTaxonomyType;

		return taxonomyType === TAXONOMY_TYPES.CLIENT;
	};

	handleViewTransactionsClick = row => {
		const { showExplorePage, queryParams, spendType } = this.props;
		const options = { vendorName: row.vendorName };

		if (this.isCustomCategoryRow(row)) {
			options.isCustom = true;
		}

		if (spendType != null) {
			options.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (queryParams.hierarchy && queryParams.hierarchy.length > 0) {
			options.hierarchy = queryParams.hierarchy;
		}

		if (queryParams.getTailSpendOnly) {
			options.isTailSpend = true;
		}

		showExplorePage(options, row.vendorName);
	};

	getVendorTransactionsParams = () => {
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: ['harmonizedSupplier'],
			filterValues: [categorisationModalData.vendorName],
		};
	};

	getContextParams = () => {
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		const apiParams = this.getApiParams();
		const apiParamsContext = {
			dateFrom: apiParams.dateFrom,
			dateTo: apiParams.dateTo,
			hierarchy: apiParams.hierarchy,
			filters: apiParams.filters,
			filterValues: apiParams.filterValues,
		};

		return {
			...apiParamsContext,
			filters: [...apiParamsContext.filters, 'harmonizedSupplier'],
			filterValues: [
				...apiParamsContext.filterValues,
				categorisationModalData.vendorName,
			],
		};
	};

	handleChangeCategorisation = ({ taxonomy, isDirect, isAllTransactions }) => {
		const params = isAllTransactions
			? this.getVendorTransactionsParams()
			: this.getContextParams();
		const spendType = isDirect
			? SPEND_TYPE_VALUE.DIRECT
			: SPEND_TYPE_VALUE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };
		if (this.props.queryParams?.indirectTaxonomyType === 2) {
			const data = {
				l1Category: taxonomy.project_L1Category,
				l2Category: taxonomy.project_L2Category,
				l3Category: taxonomy.project_L3Category,
				l4Category: taxonomy.project_L4Category,
			};
			return changeCategorisationIndirectClient(spendType, params, data).then(
				() => {
					const { refreshContent } = this.context;
					if (refreshContent) {
						refreshContent();
					}
				}
			);
		} else {
			return changeCategorisation(spendType, params, data).then(() => {
				const { refreshContent } = this.context;
				if (refreshContent) {
					refreshContent();
				}
			});
		}
	};

	handleChangeStatus = row => {
		const { queryParams, spendType } = this.props;
		const data = {
			dimensions: convertToArray(queryParams.dimensions),
			includeCustomCategories: isCustomCategory(queryParams.dimensions),
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(queryParams.hierarchy && queryParams.hierarchy.length > 0
				? { hierarchy: queryParams.hierarchy }
				: {}),
		};

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	handleMoveToBin = row => {
		const { queryParams, spendType } = this.props;

		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(queryParams.hierarchy && queryParams.hierarchy.length > 0
				? { hierarchy: queryParams.hierarchy }
				: {}),
		};

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	showCategorisationModal = categorisationModalData => {
		this.setState({ categorisationModalData });
	};

	hideCategorisationModal = () => {
		this.setState({ categorisationModalData: null });
	};

	render() {
		const {
			isLoading,
			data,
			categorisationModalData,
			changeStatusModalData,
			moveToBinModalData,
			totalCount,
		} = this.state;
		const { refreshContent } = this.context;
		const { permissions, queryParams } = this.props;

		return (
			<React.Fragment>
				<CommonVendorsTable
					isLoading={isLoading}
					data={data}
					totalCount={totalCount}
					permissions={permissions}
					loadMore={this.handleLoadMore}
					onSortChanged={this.handleSortChanged}
					columnDefs={this.getColumnDefs()}
					showCategorisationModal={this.showCategorisationModal}
					onViewTransactionsClick={this.handleViewTransactionsClick}
					onChangeStatus={this.handleChangeStatus}
					onMoveToBin={this.handleMoveToBin}
					queryParams={queryParams}
				/>
				{categorisationModalData && (
					<CategorisationDialog
						name={categorisationModalData.vendorName}
						queryParams={queryParams}
						onClose={this.hideCategorisationModal}
						onSave={this.handleChangeCategorisation}
						contextParams={this.getContextParams()}
						allTransactionsParams={this.getVendorTransactionsParams()}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						{...changeStatusModalData}
						onClose={() => {
							this.setState({ changeStatusModalData: null });
						}}
						onSubmit={refreshContent}
						isPmi
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						{...moveToBinModalData}
						onClose={() => {
							this.setState({ moveToBinModalData: null });
						}}
						onSubmit={refreshContent}
						isPmi
					/>
				)}
			</React.Fragment>
		);
	}
}

export default props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<CommonVendorsTableContainer {...props} permissions={permissions || []} />
		)}
	</PermissionsContext.Consumer>
);
